import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { motion } from "framer-motion";

import {
    Box,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Button,
    Chip,
    CircularProgress,
    Typography,
    InputAdornment,
    Stack,
    useTheme,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    IconButton,
    TablePagination
} from "@mui/material";
import {
    Search as SearchIcon,
    Person as PersonIcon,
    AssignmentInd as AssignmentIndIcon,
    Close as CloseIcon
} from "@mui/icons-material";
import { fetchEmployeeRoles, fetchAvailableRoles, assignRoleToEmployee } from "store/features/company/roleManagement/employeeRoleActions";
import {
    selectEmployeeRoles,
    selectEmployeeRolesLoading,
    selectAvailableRoles,
    selectAssigningRole
} from "store/features/company/roleManagement/employeeRoleSelectors";
import employeesSvg from "assets/folders/employees.svg";
import usePermissions from "hooks/usePermissions";

const EmployeeRoles = () => {
    const theme = useTheme();
    const dispatch = useDispatch();

    const employees = useSelector(selectEmployeeRoles) || [];
    const loading = useSelector(selectEmployeeRolesLoading);
    const assigningRole = useSelector(selectAssigningRole);
    const roles = useSelector(selectAvailableRoles) || [];

    const [searchTerm, setSearchTerm] = useState("");
    const [openModal, setOpenModal] = useState(false);
    const [selectedEmployee, setSelectedEmployee] = useState(null);
    const [selectedRoleId, setSelectedRoleId] = useState("");
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const { hasPermission, hasAnyPermission } = usePermissions();

    useEffect(() => {
        setPage(0);
    }, [searchTerm]);

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    useEffect(() => {
        dispatch(fetchEmployeeRoles());
        dispatch(fetchAvailableRoles());
    }, [dispatch]);

    const filteredEmployees = employees.filter((emp) => {
    const search = searchTerm.toLowerCase();

    const fullName =
        `${emp.first_name || ""} ${emp.last_name || ""}`.toLowerCase();

    return (
        search === "" ||
        fullName.includes(search) ||
        emp.first_name?.toLowerCase().includes(search) ||
        emp.last_name?.toLowerCase().includes(search) ||
        emp.email?.toLowerCase().includes(search) ||
        emp.role_name?.toLowerCase().includes(search)
    );
});

    const handleOpenModal = (emp) => {
        setSelectedEmployee(emp);
        setSelectedRoleId(emp.role_id || "");
        setOpenModal(true);
    };

    const handleCloseModal = () => {
        setOpenModal(false);
        setSelectedEmployee(null);
        setSelectedRoleId("");
    };

    const handleAssignRole = async () => {
        if (!selectedEmployee || !selectedRoleId) return;

        const payload = {
            employee_id: selectedEmployee.id,
            role_id: selectedRoleId
        };

        const result = await dispatch(assignRoleToEmployee(payload));
        if (result?.success) {
            handleCloseModal();
        }
    };

    if (!hasPermission("employee_roles.view")) {
        return null;
    }

    return (
        <Box
            sx={{
                minHeight: "100vh",
                // background:
                //     theme.palette.mode === "dark"
                //         ? "linear-gradient(135deg, #0a0f1e 0%, #0f172a 50%, #1e1b2e 100%)"
                //         : "linear-gradient(135deg, #fffbeb 0%, #ffffff 50%, #fefce8 100%)",
                p: 3,
            }}
        >
            {/* Header Section */}
            <Box sx={{ mb: 4 }}>
                <Stack
                    direction={{ xs: "column", sm: "row" }}
                    spacing={2}
                    alignItems={{ sm: "center" }}
                    justifyContent="space-between"
                    sx={{ width: "100%" }}
                >
                    <Stack
                        direction="row"
                        spacing={2}
                        alignItems="center"
                        sx={{ flex: 1 }}
                    >
                        <Box
                            component="img"
                            src={employeesSvg}
                            alt="Employees"
                            sx={{
                                width: { xs: 64, lg: 80 },
                                height: { xs: 64, lg: 80 },
                                objectFit: "contain",
                            }}
                            onError={(e) => {
                                e.target.style.display = "none";
                            }}
                        />
                        <Box>
                            <Typography
                                variant="h4"
                                component="h1"
                                sx={{
                                    fontWeight: "bold",
                                    color: "text.primary",
                                }}
                            >
                                Employee Roles
                            </Typography>
                            <Typography
                                variant="body2"
                                sx={{
                                    color: "text.secondary",
                                    mt: 0.5,
                                }}
                            >
                                Manage roles assigned to employees
                            </Typography>
                        </Box>
                    </Stack>
                </Stack>
            </Box>

            {/* Search Section */}
            <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={2}
                alignItems={{ sm: "center" }}
                justifyContent="space-between"
                sx={{ mb: 3 }}
            >
                <TextField
                    placeholder="Search by name, email or role..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    variant="outlined"
                    size="small"
                    sx={{
                        flex: 1,
                        maxWidth: { sm: 400 },
                        "& .MuiOutlinedInput-root": {
                            borderRadius: "12px",
                            bgcolor: "background.paper",
                            "& fieldset": {
                                borderColor:
                                    theme.palette.mode === "dark" ? "#334155" : "#e5e7eb",
                            },
                            "&:hover fieldset": {
                                borderColor: "#f59e0b",
                            },
                            "&.Mui-focused fieldset": {
                                borderColor: "#f59e0b",
                                borderWidth: "2px",
                            },
                        },
                    }}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon sx={{ color: "#9ca3af" }} />
                            </InputAdornment>
                        ),
                    }}
                />
            </Stack>

            {/* Loading State */}
            {loading ? (
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        height: 400,
                    }}
                >
                    <Stack alignItems="center" spacing={2}>
                        <CircularProgress
                            size={48}
                            sx={{
                                color: "#3b82f6",
                                "& .MuiCircularProgress-circle": {
                                    strokeLinecap: "round",
                                },
                            }}
                        />
                        <Typography
                            sx={{
                                color: "text.secondary",
                            }}
                        >
                            Loading employees...
                        </Typography>
                    </Stack>
                </Box>
            ) : (
                <Paper
                    elevation={0}
                    sx={{
                        borderRadius: "16px",
                        overflow: "hidden",
                        boxShadow:
                            theme.palette.mode === "dark"
                                ? "0 10px 25px -5px rgba(0,0,0,0.3)"
                                : "0 10px 25px -5px rgba(0,0,0,0.05), 0 8px 10px -6px rgba(0,0,0,0.02)",
                        border: `1px solid ${theme.palette.mode === "dark" ? "#1e293b" : "#f3f4f6"
                            }`,
                    }}
                >
                    <TableContainer sx={{ overflowX: "auto" }}>
                        <Table sx={{ minWidth: 800 }}>
                            <TableHead
                                sx={{
                                    backgroundColor:
                                        theme.palette.mode === "dark" ? "#1e293b" : "#f9fafb",
                                }}
                            >
                                <TableRow>
                                    <TableCell sx={{ fontWeight: 600, fontSize: "0.75rem" }}>
                                        ID
                                    </TableCell>
                                    <TableCell
                                        align="left"
                                        sx={{
                                            fontWeight: 600,
                                            fontSize: "0.75rem",
                                        }}
                                    >
                                        Employee Info
                                    </TableCell>
                                    <TableCell
                                        sx={{
                                            fontWeight: 600,
                                            fontSize: "0.75rem",
                                        }}
                                    >
                                        Current Role
                                    </TableCell>
                                    {hasAnyPermission(["employee_roles.update", "employee_roles.delete"]) && (
                                        <TableCell
                                            align="center"
                                            sx={{
                                                fontWeight: 600,
                                                fontSize: "0.75rem",
                                            }}
                                        >
                                            Action
                                        </TableCell>
                                    )}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {filteredEmployees.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={4} align="center" sx={{ py: 6 }}>
                                            <PersonIcon
                                                sx={{ fontSize: 48, color: "#d1d5db", mb: 1 }}
                                            />
                                            <Typography
                                                sx={{
                                                    color: "text.secondary",
                                                }}
                                            >
                                                No employees found.
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    filteredEmployees.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((emp, index) => (
                                        <TableRow
                                            key={emp.id}
                                            component={motion.tr}
                                            initial={{ opacity: 0, x: -20 }}
                                            animate={{ opacity: 1, x: 0 }}
                                            transition={{ delay: index * 0.05 }}
                                            sx={{
                                                "&:hover": {
                                                    backgroundColor:
                                                        theme.palette.mode === "dark"
                                                            ? "rgba(30,41,59,0.5)"
                                                            : "#f9fafb",
                                                },
                                            }}
                                        >
                                            <TableCell>
                                                <Typography
                                                    sx={{
                                                        fontFamily: "monospace",
                                                        fontWeight: 700,
                                                        fontSize: "0.875rem",
                                                        color: "primary.main",
                                                    }}
                                                >
                                                    #{emp.id}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Stack
                                                    direction="row"
                                                    spacing={1.5}
                                                    alignItems="center"
                                                >
                                                    <Box
                                                        sx={{
                                                            width: 40,
                                                            height: 40,
                                                            borderRadius: "12px",
                                                            display: "flex",
                                                            alignItems: "center",
                                                            justifyContent: "center",
                                                        }}
                                                    >
                                                        <PersonIcon
                                                            sx={{ fontSize: 28, color: "#3b82f6" }}
                                                        />
                                                    </Box>
                                                    <Box>
                                                        <Typography
                                                            sx={{
                                                                fontWeight: 600,
                                                                color: "text.primary",
                                                            }}
                                                        >
                                                             {emp.first_name} {emp.last_name}
                                                        </Typography>
                                                        <Typography
                                                            sx={{
                                                                fontSize: "0.75rem",
                                                                color: "text.secondary",
                                                            }}
                                                        >
                                                            {emp.email}
                                                        </Typography>
                                                    </Box>
                                                </Stack>
                                            </TableCell>
                                            <TableCell>
                                                <Stack spacing={0.5}>
                                                    {emp.role_id ? (
                                                        <>
                                                            <Chip
                                                                label={emp.role_name}
                                                                size="small"
                                                                sx={{
                                                                    backgroundColor:
                                                                        theme.palette.mode === "dark"
                                                                            ? "rgba(34,197,94,0.15)"
                                                                            : "#dcfce7",
                                                                    color:
                                                                        theme.palette.mode === "dark"
                                                                            ? "#86efac"
                                                                            : "#166534",
                                                                    fontWeight: 600,
                                                                    fontSize: "0.75rem",
                                                                    width: "fit-content",
                                                                }}
                                                            />
                                                        </>
                                                    ) : (
                                                        <Chip
                                                            label="No Role"
                                                            size="small"
                                                            sx={{
                                                                backgroundColor:
                                                                    theme.palette.mode === "dark"
                                                                        ? "rgba(239,68,68,0.15)"
                                                                        : "#fee2e2",
                                                                color:
                                                                    theme.palette.mode === "dark"
                                                                        ? "#fca5a5"
                                                                        : "#b91c1c",
                                                                fontWeight: 500,
                                                                fontSize: "0.7rem",
                                                                width: "fit-content",
                                                            }}
                                                        />
                                                    )}
                                                </Stack>
                                            </TableCell>
                                            <TableCell align="center">
                                                {hasAnyPermission(["employee_roles.create", "employee_roles.update"]) && (
                                                    <Button
                                                        variant="contained"
                                                        startIcon={<AssignmentIndIcon />}
                                                        onClick={() => handleOpenModal(emp)}
                                                        sx={{
                                                            background: !emp.role
                                                                ? "linear-gradient(135deg, #10b981 0%, #059669 100%)"
                                                                : theme.palette.mode === "dark"
                                                                    ? "linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)"
                                                                    : "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
                                                            color: "#ffffff",
                                                            borderRadius: "100px",
                                                            textTransform: "none",
                                                            px: 2.5,
                                                            py: 0.5,
                                                            fontSize: "0.8rem",
                                                            fontWeight: 500,
                                                            boxShadow: "none",
                                                            transition: "all 0.2s ease-in-out",
                                                            "&:hover": {
                                                                background: !emp.role
                                                                    ? "linear-gradient(135deg, #059669 0%, #047857 100%)"
                                                                    : theme.palette.mode === "dark"
                                                                        ? "linear-gradient(135deg, #2563eb 0%, #1e40af 100%)"
                                                                        : "linear-gradient(135deg, #d97706 0%, #b45309 100%)",
                                                                transform: "translateY(-1px)",
                                                            },
                                                        }}
                                                    >
                                                        {emp.role ? "Change Role" : "Assign Role"}
                                                    </Button>
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <TablePagination
                        rowsPerPageOptions={[5, 10, 25, 50]}
                        component="div"
                        count={filteredEmployees.length}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={handleChangePage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                    />
                </Paper>
            )}

            {/* Count Summary */}
            {!loading && employees.length > 0 && (
                <Stack
                    direction="row"
                    justifyContent="space-between"
                    sx={{
                        mt: 2,
                        gap: 2,
                        fontSize: "0.875rem",
                        color: "text.secondary",
                    }}
                >
                    <Typography variant="body2">
                        Total Employees: {employees.length}
                    </Typography>
                    <Typography variant="body2">
                        Showing: {filteredEmployees.length} of {employees.length}
                    </Typography>
                </Stack>
            )}

            {/* Assign Role Modal */}
            <Dialog
                open={openModal}
                onClose={handleCloseModal}
                maxWidth="sm"
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: "16px",
                        bgcolor: theme.palette.mode === "dark" ? "#1e293b" : "#ffffff",
                        backgroundImage: "none",
                    },
                }}
            >
                <DialogTitle
                    sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        pb: 2,
                        borderBottom: `1px solid ${theme.palette.mode === "dark" ? "#334155" : "#f3f4f6"
                            }`,
                    }}
                >
                    <Box>
                        <Typography variant="h6" fontWeight="bold">
                            {selectedEmployee?.role ? "Change Employee Role" : "Assign Employee Role"}
                        </Typography>
                        {selectedEmployee && (
                            <Typography
                                variant="body2"
                                sx={{
                                    color: "text.secondary",
                                }}
                            >
                                Employee: <Box component="span" sx={{ color: theme.palette.mode === "dark" ? "#fcd34d" : "#d97706", fontWeight: "bold" }}>{selectedEmployee.first_name}</Box>
                            </Typography>
                        )}
                    </Box>
                    <IconButton onClick={handleCloseModal} size="small">
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent sx={{ p: 3, bgcolor: theme.palette.mode === "dark" ? "#0f172a" : "#f8fafc" }}>
                    <Box sx={{ mt: 3 }}>
                        <FormControl fullWidth variant="outlined" size="medium">
                            <InputLabel id="role-select-label">Select Role</InputLabel>
                            <Select
                                labelId="role-select-label"
                                value={selectedRoleId}
                                onChange={(e) => setSelectedRoleId(e.target.value)}
                                label="Select Role"
                                sx={{
                                    bgcolor: theme.palette.mode === "dark" ? "#1e293b" : "#ffffff",
                                    "& .MuiOutlinedInput-notchedOutline": {
                                        borderColor: theme.palette.mode === "dark" ? "#334155" : "#e5e7eb",
                                    },
                                    "&:hover .MuiOutlinedInput-notchedOutline": {
                                        borderColor: "#f59e0b",
                                    },
                                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                        borderColor: "#f59e0b",
                                    },
                                }}
                            >
                                <MenuItem value="">
                                    <em>None</em>
                                </MenuItem>
                                {roles.map((role) => (
                                    <MenuItem key={role.id} value={role.id}>
                                        {role.name} {role.code && `(${role.code})`}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Box>
                </DialogContent>
                <DialogActions
                    sx={{
                        p: 2.5,
                        borderTop: `1px solid ${theme.palette.mode === "dark" ? "#334155" : "#f3f4f6"
                            }`,
                        bgcolor: theme.palette.mode === "dark" ? "#1e293b" : "#ffffff",
                    }}
                >
                    <Button
                        onClick={handleCloseModal}
                        sx={{
                            color: "text.secondary",
                            textTransform: "none",
                            fontWeight: 500,
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleAssignRole}
                        disabled={assigningRole || !selectedRoleId}
                        sx={{
                            background: "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
                            color: "#ffffff",
                            borderRadius: "8px",
                            textTransform: "none",
                            px: 3,
                            fontWeight: 600,
                            boxShadow: "none",
                            "&:hover": {
                                background: "linear-gradient(135deg, #d97706 0%, #b45309 100%)",
                                boxShadow: "0 4px 12px rgba(245, 158, 11, 0.3)",
                            },
                            "&.Mui-disabled": {
                                background: theme.palette.mode === "dark" ? "#334155" : "#e5e7eb",
                                color: theme.palette.mode === "dark" ? "#9ca3af" : "#9ca3af",
                            }
                        }}
                    >
                        {assigningRole ? <CircularProgress size={24} color="inherit" /> : "Save Role"}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default EmployeeRoles;